/**
 * Always-visible sound toggle. Icon only; the words live in aria-label and title.
 *
 * Three honest states:
 *   off   — sound not wanted (crossed out)
 *   armed — sound wanted but no gesture has happened yet (slow gold pulse; "click anywhere")
 *   on    — audible; the two sound-wave arcs double as a small level meter
 * Clicking when off enables AND unlocks (the click is the gesture). The M key is handled by
 * main.ts; we only mention it.
 */
import '../styles/ui.css';
import type { Bus } from '../bus';
import type { AudioHandle } from '../audio/engine';

export interface MuteOptions {
  mount: HTMLElement;
  audio: AudioHandle;
  bus: Bus;
}

const ICON = `
<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
  <g class="tube">
    <line x1="10.5" y1="1.5" x2="10.5" y2="4.5"/>
    <rect x="8" y="4.5" width="5" height="16" rx="2.5"/>
  </g>
  <path class="wave wave-1" d="M16.5 8.5c1.9 2.3 1.9 5.7 0 8"/>
  <path class="wave wave-2" d="M19.6 5.8c3.3 3.9 3.3 9.5 0 13.4"/>
  <line class="slash" x1="4" y1="3.5" x2="21" y2="20.5"/>
</svg>`;

const isTouch = () =>
  document.documentElement.dataset.touch === '1' ||
  (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches);

export function createMuteButton(opts: MuteOptions): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.id = 'mute';
  btn.className = 'sound-toggle';
  btn.innerHTML = ICON;
  const gesture = isTouch() ? 'tap' : 'click';

  let raf = 0;
  const meterTick = () => {
    raf = 0;
    if (!opts.audio.state.audible) {
      btn.style.setProperty('--lvl1', '0.35');
      btn.style.setProperty('--lvl2', '0.18');
      return;
    }
    const { l, r } = opts.audio.levels();
    const v = Math.max(l, r);
    const lvl1 = 0.3 + 0.7 * Math.min(1, v * 2);
    const lvl2 = 0.14 + 0.86 * Math.min(1, Math.max(0, v * 2 - 0.55));
    btn.style.setProperty('--lvl1', lvl1.toFixed(3));
    btn.style.setProperty('--lvl2', lvl2.toFixed(3));
    raf = requestAnimationFrame(meterTick);
  };

  const paint = () => {
    const s = opts.audio.state;
    const mode = s.audible ? 'on' : s.enabled ? 'armed' : 'off';
    btn.classList.toggle('is-on', mode === 'on');
    btn.classList.toggle('is-armed', mode === 'armed');
    btn.classList.toggle('is-off', mode === 'off');
    btn.setAttribute('aria-pressed', String(s.enabled));
    let label: string;
    let title: string;
    if (mode === 'on') {
      label = 'Sound on. Mute';
      title = `sound on — ${gesture} to mute (M)`;
    } else if (mode === 'armed') {
      label = `Sound is ready. ${gesture} anywhere to let it ring`;
      title = `${gesture} anywhere to let it ring`;
    } else {
      label = 'Sound off. Turn sound on';
      title = `sound off — ${gesture} to let it ring (M)`;
    }
    btn.setAttribute('aria-label', label);
    btn.title = title;
    if (mode === 'on' && !raf) raf = requestAnimationFrame(meterTick);
    if (mode !== 'on' && raf) { cancelAnimationFrame(raf); raf = 0; meterTick(); }
  };

  // When the button is pressed while armed, the press IS the gesture: it must ring, never
  // mute — even if unlock() completes (asynchronously) before the click event arrives.
  let armedPress = 0;
  const markArmedPress = () => {
    const s = opts.audio.state;
    if (s.enabled && !s.unlocked) {
      if (armedPress) clearTimeout(armedPress);
      armedPress = window.setTimeout(() => { armedPress = 0; }, 1500);
    }
  };
  btn.addEventListener('pointerdown', (e) => {
    // keep the press out of main.ts's hand tracking and its own unlock path
    e.stopPropagation();
    markArmedPress();
    if (armedPress) void opts.audio.unlock();
  });
  btn.addEventListener('keydown', (e) => {
    if (e.key === ' ' || e.key === 'Enter') markArmedPress();
  });
  btn.addEventListener('click', () => {
    const s = opts.audio.state;
    if (armedPress) {
      clearTimeout(armedPress);
      armedPress = 0;
      void opts.audio.unlock();
      return;
    }
    if (!s.enabled) {
      opts.audio.setEnabled(true);
      void opts.audio.unlock();
    } else if (!s.unlocked) {
      void opts.audio.unlock();
    } else {
      opts.audio.setEnabled(false);
    }
  });

  opts.bus.on('audio', paint);
  paint();
  opts.mount.appendChild(btn);
  return btn;
}
